import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import Navigation from './ComponentsBevego/Navigation';
import MiniCart from './ComponentsBevego/MiniCart';
import MenuUser from './ComponentsBevego/MenuUser';
import QuickSearch from './ComponentsBevego/QuickSearch';
import FacetedSearch from './ComponentsBevego/FacetedSearch';
import FacetedSearchTags from './ComponentsBevego/FacetedSearchTags';
import FacetedSearchCompactContainer from './ComponentsBevego/FacetedSearchCompactContainer';
import DynamicComponent from './ComponentsBevego/DynamicComponent';
import AffiliatePicker from './ComponentsBevego/AffiliatePicker';

let lightBoxImagesInitiated = false;
window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }

    var menuUser = document.getElementById('menu-user');
    if (menuUser && !menuUser.init) {
        menuUser.init = true;
        renderReact(
            <Provider store={store}>
                <MenuUser content={menuUser.innerHTML} />
            </Provider>,
            document.getElementById('menu-user')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }

    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('facetedSearchTags')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchTags />
            </Provider>,
            document.getElementById('facetedSearchTags')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./ComponentsBevego/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./ComponentsBevego/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./ComponentsBevego/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }

    var lightBoxImagesEl = document.getElementById('lightBoxImages');

    if (lightBoxImagesEl && !lightBoxImagesInitiated) {
        lightBoxImagesInitiated = true;
        const LightboxImages = DynamicComponent({
            loader: () => import('./ComponentsBevego/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('template')
                ).map((img) => ({
                    html: img.innerHTML,
                    src: img.dataset.src,
                }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages images={images} />
                    </Provider>,
                    lightBoxImagesEl
                );
            }
        );
    }

    const productQuantityEl = document.querySelectorAll(
        '.product__quantity-container'
    );
    if (productQuantityEl.length > 0) {
        const ProductQuantity = DynamicComponent({
            loader: () => import('./ComponentsBevego/ProductQuantity'),
        });
        Array.from(productQuantityEl).forEach((productQuantity) => {
            const {
                quantityFieldId,
                multiple,
                inStockQuantity,
                unitOfMeasurement,
                decimalAmount
            } = productQuantity.dataset;
            const multipleValue = multiple ? multiple.replace(',', '.') : 1;
            renderReact(
                <Provider store={store}>
                    <ProductQuantity
                        {...{
                            quantityFieldId,
                            multipleValue,
                            inStockQuantity,
                            unitOfMeasurement,
                            decimalAmount
                        }}
                    />
                </Provider>,
                productQuantity
            );
        });
    }

    var sliders = document.querySelectorAll('.slider');
    if (sliders.length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./ComponentsBevego/Slider'),
        });
        let sliderContainsBuyButtons = false;
        Array.from(sliders).forEach((slider, index) => {
            const values = [...slider.querySelectorAll('template')].map((block) => {
                if (block.innerHTML.indexOf('buy-button') > -1) {
                    sliderContainsBuyButtons = true;
                }
                return {
                    html: block.innerHTML,
                };
            });
            if (values.length > 0) {
                renderReact(<Slider values={values} bootstrapComponents={sliderContainsBuyButtons} />, slider);
            }
        });
    }

    if (document.getElementById('shoppingListPage')) {
        const ShoppingList = DynamicComponent({
            loader: () => import('./ComponentsBevego/ShoppingListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <ShoppingList />
            </Provider>,
            document.getElementById('shoppingListPage')
        );
    }

    if (document.getElementById('quoteHistoryPage')) {
        const QuoteList = DynamicComponent({
            loader: () =>
                import('./ComponentsBevego/QuoteHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <QuoteList />
            </Provider>,
            document.getElementById('quoteHistoryPage')
        );
    }

    if (document.getElementById('preliminaryOrderHistoryPage')) {
        const PreliminaryOrderList = DynamicComponent({
            loader: () =>
                import(
                    './ComponentsBevego/PreliminaryOrderHistoryListContainer'
                ),
        });
        renderReact(
            <Provider store={store}>
                <PreliminaryOrderList />
            </Provider>,
            document.getElementById('preliminaryOrderHistoryPage')
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () =>
                import('./ComponentsBevego/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    if (document.getElementById('invoiceHistoryPage')) {
        const InvoiceList = DynamicComponent({
            loader: () =>
                import('./ComponentsBevego/InvoiceHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <InvoiceList />
            </Provider>,
            document.getElementById('invoiceHistoryPage')
        );
    }

    if (document.getElementById('js-userDetailsPage')) {
        const UserDetails = DynamicComponent({
            loader: () => import('./ComponentsBevego/UserDetails'),
        });
        renderReact(
            <Provider store={store}>
                <UserDetails />
            </Provider>,
            document.getElementById('js-userDetailsPage')
        );
    }

    var favouriteListEl = document.getElementById('js-favouriteListPage');
    if (favouriteListEl != null) {
        const FavouriteList = DynamicComponent({
            loader: () => import('./ComponentsBevego/FavouriteList'),
        });
        renderReact(
            <Provider store={store}>
                <FavouriteList />
            </Provider>,
            favouriteListEl
        );
    }
    const favouriteButtons = document.querySelectorAll('favourite-button');
    if (favouriteButtons.length > 0) {
        const FavouriteButton = DynamicComponent({
            loader: () => import('./ComponentsBevego/FavouriteButton'),
        });
        Array.from(favouriteButtons).forEach((button) => {
            const {
                articleNumber,
                favouriteClass,
                cssClass
            } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <FavouriteButton
                        {...{ articleNumber, favouriteClass, cssClass }}
                    />
                </Provider>,
                button
            );
        });
    }
    if (document.getElementById('js-affiliatePicker')) {
        renderReact(
            <Provider store={store}>
                <AffiliatePicker />
            </Provider>,
            document.getElementById('js-affiliatePicker')
        );
    }

    var affiliateStocks = document.querySelectorAll('#js-affiliateStock');
    if (affiliateStocks.length > 0) {
        const AffiliateStock = DynamicComponent({
            loader: () => import('./ComponentsBevego/AffiliateStock'),
        });
        Array.from(affiliateStocks).forEach((affiliateStock) => {
            const { variantid } = affiliateStock.dataset;
            renderReact(
                <Provider store={store}>
                    <AffiliateStock variantId={variantid} />
                </Provider>,
                affiliateStock
            );
        });
    }

    var productListSorting = document.getElementById('js-productListSorting');
    if (productListSorting != null) {
        const ProductListSorting = DynamicComponent({
            loader: () => import('./ComponentsBevego/ProductListSorting'),
        });
        renderReact(
            <Provider store={store}>
                <ProductListSorting />
            </Provider>,
            productListSorting
        );
    }

    var selectOrganization = document.getElementById('js-selectorganization');
    if (selectOrganization != null) {
        const SelectOrganization = DynamicComponent({
            loader: () => import('./ComponentsBevego/SelectOrganization'),
        });
        renderReact(
            <Provider store={store}>
                <SelectOrganization />
            </Provider>,
            selectOrganization
        );
    }

    if (document.querySelectorAll('contact-button').length > 0) {
        const ContactAffiliatePickerDialog = DynamicComponent({
            loader: () =>
                import('./ComponentsBevego/ContactAffiliatePickerDialog'),
        });
        const ContactAffiliateButton = DynamicComponent({
            loader: () => import('./ComponentsBevego/ContactAffiliateButton'),
        });

        renderReact(
            <Provider store={store}>
                <ContactAffiliatePickerDialog />
            </Provider>,
            document.getElementById('js-contactaffiliatedialog')
        );

        Array.from(document.querySelectorAll('contact-button')).forEach(
            (button) => {
                const { articleNumber, cssClass, label } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <ContactAffiliateButton
                            {...{
                                label,
                                articleNumber,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./ComponentsBevego/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    var lazyLoading = document.getElementById('lazyloader');
    if (lazyLoading) {
        import('./pagination');
    }

    var downloadDocuments = document.getElementById('js-downloaddocuments');
    if (downloadDocuments != null) {
        const DownloadDocumentsContainer = DynamicComponent({
            loader: () =>
                import('./ComponentsBevego/DownloadDocumentsContainer'),
        });
        const {
            customerOrderNumber,
            externalOrderId,
            orderDate,
            buttonText,
        } = downloadDocuments.dataset;
        renderReact(
            <Provider store={store}>
                <DownloadDocumentsContainer
                    customerOrderNumber={customerOrderNumber}
                    externalOrderID={externalOrderId}
                    orderDate={orderDate}
                    buttonText={buttonText}
                />
            </Provider>,
            downloadDocuments
        );
    }

    var silencium = document.getElementById('js-silencium');
    if (silencium != null) {
        const SilenciumContainer = DynamicComponent({
            loader: () => import('./ComponentsBevego/SilenciumContainer'),
        });
        renderReact(
            <Provider store={store}>
                <SilenciumContainer />
            </Provider>,
            silencium
        );
    }
    const modalEl = document.querySelectorAll('.js-generic-modal');
    if (modalEl.length > 0) {
        const GenericModal = DynamicComponent({
            loader: () => import('./ComponentsBevego/GenericModal')
        });
        Array.from(modalEl).forEach(
            (modal) => {
                const modalText = modal.innerHTML;
                const { modalLink } = modal.dataset;
                renderReact(
                    <Provider store={store}>
                        <GenericModal
                            {...{ modalLink, modalText }}
                        />
                    </Provider>,
                    modal
                );
            }
        );
    }
    var variantItemsEl = document.getElementById('js-variant-items');
    if (variantItemsEl != null) {
        const VariantItems = DynamicComponent({
            loader: () => import('./ComponentsBevego/VariantItems'),
        });
        renderReact(
            <Provider store={store}>
                <VariantItems />
            </Provider>,
            variantItemsEl
        );
    }
};

document.addEventListener('click',
    event => {
        if (!event.target.matches(".product-detail__read-more")) return;
        const productSpecificationEl = document.getElementById("product-product-specification");
        if (productSpecificationEl) {
            productSpecificationEl.checked = true;
        }
    });

bootstrapComponents();
